import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import {
  buildLegalContentPagePayloadFromForm,
  mapLegalContentPageListResponseToDto,
  mapLegalContentPageResponseToDto,
  mapLegalDocumentsResponseToDto,
  mapSystemLegalFieldErrors,
  validateLegalContentPagePayload,
} from '../dto/system-legal.dto.js';
import {
  LEGAL_PAGE_TABS,
  buildEmptyLegalContentForm,
  buildLegalContentFormModel,
  buildLegalDocumentRowsModel,
  buildLegalDocumentUploadForm,
  countActiveLegalDocuments,
  normalizeLegalHtmlForSave,
  validateLegalDocumentUploadForm,
} from '../model/system-legal.model.js';

const DEFAULT_CONTENT_KEY = 'privacy_policy';

function buildUploadFormData(form) {
  const formData = new FormData();
  formData.append('name', String(form?.name || '').trim());
  formData.append('version', String(form?.version || '').trim());
  formData.append('status', String(form?.status || '').trim());
  formData.append('effectiveDate', String(form?.effectiveDate || '').trim());
  formData.append('expirationDate', String(form?.expirationDate || '').trim());
  formData.append('document', form.document);
  return formData;
}

function triggerBlobDownload(response, fallbackName) {
  const url = URL.createObjectURL(response.blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = response.filename || fallbackName || 'legal-document';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function useSystemLegal() {
  const { token } = useAuth();

  const [activeTab, setActiveTab] = useState('documents');
  const [documents, setDocuments] = useState([]);
  const [contentForms, setContentForms] = useState({});
  const [uploadForm, setUploadForm] = useState(buildLegalDocumentUploadForm);
  const [uploadErrors, setUploadErrors] = useState({});
  const [contentErrors, setContentErrors] = useState({});
  const [isDocumentsLoading, setIsDocumentsLoading] = useState(true);
  const [isContentLoading, setIsContentLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloadingUid, setIsDownloadingUid] = useState('');
  const [documentsLoadError, setDocumentsLoadError] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const activeDocumentCount = useMemo(() => countActiveLegalDocuments(documents), [documents]);

  const loadDocuments = useCallback(async () => {
    setIsDocumentsLoading(true);
    setError('');
    setDocumentsLoadError('');

    try {
      const response = await adminApi.listLegalDocuments(token);
      const dto = mapLegalDocumentsResponseToDto(response);
      setDocuments(buildLegalDocumentRowsModel(dto));
    } catch (requestError) {
      if (requestError?.status === 404) {
        setDocuments([]);
      } else {
        const message = requestError.message || 'Failed to load legal documents.';
        setDocumentsLoadError(message);
        setError(message);
        setDocuments([]);
      }
    } finally {
      setIsDocumentsLoading(false);
    }
  }, [token]);

  const loadContentPages = useCallback(async () => {
    setIsContentLoading(true);
    setError('');

    try {
      const response = await adminApi.listLegalContentPages(token);
      const pages = mapLegalContentPageListResponseToDto(response);
      const nextForms = {};

      LEGAL_PAGE_TABS
        .filter((tab) => tab.key !== 'documents')
        .forEach((tab) => {
          const page = pages.find((item) => item.key === tab.key);
          nextForms[tab.key] = page ? buildLegalContentFormModel(page) : buildEmptyLegalContentForm(tab.key);
        });

      setContentForms(nextForms);
    } catch (requestError) {
      if (requestError?.status === 404) {
        const nextForms = {};
        LEGAL_PAGE_TABS
          .filter((tab) => tab.key !== 'documents')
          .forEach((tab) => {
            nextForms[tab.key] = buildEmptyLegalContentForm(tab.key);
          });
        setContentForms(nextForms);
      } else {
        setError(requestError.message || 'Failed to load legal content.');
        setContentForms({});
      }
    } finally {
      setIsContentLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadDocuments();
    loadContentPages();
  }, [loadContentPages, loadDocuments]);

  const setUploadField = useCallback((field, value) => {
    setUploadForm((prev) => ({ ...prev, [field]: value }));
    setUploadErrors((prev) => {
      if (!prev[field]) {
        return prev;
      }
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const setContentField = useCallback((key, field, value) => {
    const normalizedKey = String(key || DEFAULT_CONTENT_KEY).trim();
    setContentForms((prev) => ({
      ...prev,
      [normalizedKey]: {
        ...(prev[normalizedKey] || buildEmptyLegalContentForm(normalizedKey)),
        [field]: value,
      },
    }));
    setContentErrors((prev) => {
      if (!prev[normalizedKey]?.[field]) {
        return prev;
      }
      const next = {
        ...prev,
        [normalizedKey]: { ...prev[normalizedKey] },
      };
      delete next[normalizedKey][field];
      if (Object.keys(next[normalizedKey]).length === 0) {
        delete next[normalizedKey];
      }
      return next;
    });
  }, []);

  const uploadDocument = useCallback(async (event) => {
    event.preventDefault();

    const validationErrors = validateLegalDocumentUploadForm(uploadForm);
    if (Object.keys(validationErrors).length) {
      setUploadErrors(validationErrors);
      setError('Please fix validation errors.');
      setSuccess('');
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      await adminApi.uploadLegalDocument(token, buildUploadFormData(uploadForm));
      setUploadForm(buildLegalDocumentUploadForm());
      setUploadErrors({});
      setSuccess('Legal document uploaded.');
      await loadDocuments();
    } catch (requestError) {
      setUploadErrors(mapSystemLegalFieldErrors(requestError));
      setError(requestError.message || 'Failed to upload legal document.');
    } finally {
      setIsSaving(false);
    }
  }, [loadDocuments, token, uploadForm]);

  const downloadDocument = useCallback(async (row) => {
    const uid = String(row?.uid || '').trim();
    if (!uid) {
      return;
    }

    setIsDownloadingUid(uid);
    setError('');
    setSuccess('');

    try {
      const response = await adminApi.downloadLegalDocument(token, uid);
      triggerBlobDownload(response, row?.name || uid);
      setSuccess('Legal document download started.');
    } catch (requestError) {
      setError(requestError.message || 'Failed to download legal document.');
    } finally {
      setIsDownloadingUid('');
    }
  }, [token]);

  const refreshContentPage = useCallback(async (key) => {
    const normalizedKey = String(key || DEFAULT_CONTENT_KEY).trim();
    setIsContentLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await adminApi.getLegalContentPage(token, normalizedKey);
      const page = mapLegalContentPageResponseToDto(response);
      setContentForms((prev) => ({
        ...prev,
        [normalizedKey]: buildLegalContentFormModel(page),
      }));
    } catch (requestError) {
      if (requestError?.status === 404) {
        setContentForms((prev) => ({
          ...prev,
          [normalizedKey]: buildEmptyLegalContentForm(normalizedKey),
        }));
      } else {
        setError(requestError.message || 'Failed to refresh legal content.');
      }
    } finally {
      setIsContentLoading(false);
    }
  }, [token]);

  const saveContentPage = useCallback(async (key) => {
    const normalizedKey = String(key || DEFAULT_CONTENT_KEY).trim();
    const form = contentForms[normalizedKey] || buildEmptyLegalContentForm(normalizedKey);
    const payload = buildLegalContentPagePayloadFromForm({
      ...form,
      html: normalizeLegalHtmlForSave(form.html),
    });
    const validationErrors = validateLegalContentPagePayload(payload);

    if (Object.keys(validationErrors).length) {
      setContentErrors((prev) => ({
        ...prev,
        [normalizedKey]: validationErrors,
      }));
      setError('Please fix validation errors.');
      setSuccess('');
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await adminApi.updateLegalContentPage(token, normalizedKey, payload);
      const page = mapLegalContentPageResponseToDto(response);
      setContentForms((prev) => ({
        ...prev,
        [normalizedKey]: buildLegalContentFormModel(page),
      }));
      setContentErrors((prev) => {
        if (!prev[normalizedKey]) {
          return prev;
        }
        const next = { ...prev };
        delete next[normalizedKey];
        return next;
      });
      setSuccess('Legal content saved.');
    } catch (requestError) {
      setContentErrors((prev) => ({
        ...prev,
        [normalizedKey]: mapSystemLegalFieldErrors(requestError),
      }));
      setError(requestError.message || 'Failed to save legal content.');
    } finally {
      setIsSaving(false);
    }
  }, [contentForms, token]);

  return {
    tabs: LEGAL_PAGE_TABS,
    activeTab,
    setActiveTab,
    documents,
    activeDocumentCount,
    uploadForm,
    uploadErrors,
    setUploadField,
    uploadDocument,
    downloadDocument,
    isDocumentsLoading,
    isContentLoading,
    isSaving,
    isDownloadingUid,
    documentsLoadError,
    contentForms,
    contentErrors,
    setContentField,
    saveContentPage,
    refreshContentPage,
    error,
    success,
  };
}
